import type { ReactNode } from "react";
import type { TourTarget } from "./editorHelp";
import { BookOpen, Images, MessageSquare, Palette, PanelsTopLeft, UserRound } from "lucide-react";

type EditorNavigationProps = {
  onOpenSite?: () => void;
  onOpenPages?: () => void;
  onOpenDesign?: () => void;
  onOpenYou?: () => void;
  onOpenMedia?: () => void;
  onOpenAgent?: () => void;
  pagesOpen?: boolean;
  designOpen?: boolean;
  youOpen?: boolean;
  mediaOpen?: boolean;
  agentOpen?: boolean;
};

const navigationButtonClass =
  "flex min-h-12 flex-1 flex-col items-center justify-center gap-1 rounded-full font-lora text-xs leading-normal text-hazumu-muted transition-[background-color,box-shadow,color] duration-180 ease-[ease] hover:bg-hazumu-white/45 hover:text-hazumu-ink aria-[current=page]:bg-hazumu-white/70 aria-[current=page]:text-hazumu-ink aria-[current=page]:[&_svg]:text-hazumu-terracotta focus-visible:outline-2 focus-visible:outline-hazumu-ink focus-visible:outline-offset-2 motion-reduce:transition-none";

/** One editor section in the navigation, on phones and in the sidebar. */
export const NavItem = ({
  icon,
  label,
  current,
  onClick,
  tour,
}: {
  icon: ReactNode;
  label: string;
  current: boolean;
  onClick?: () => void;
  /** Lets the Getting started tour point at this item. */
  tour?: TourTarget;
}) => (
  <button
    className={navigationButtonClass}
    data-tour={tour}
    type="button"
    aria-current={current ? "page" : undefined}
    onClick={onClick}
  >
    {icon}
    <span>{label}</span>
  </button>
);

// The Ducky button only renders when onOpenAgent is provided — undefined
// keeps the flag-dark surface entirely out of the navigation.
export const EditorNavigation = ({
  onOpenSite, onOpenPages, onOpenDesign, onOpenMedia, onOpenYou, onOpenAgent, pagesOpen, designOpen, mediaOpen, youOpen, agentOpen,
}: EditorNavigationProps) => (
  <nav
    className="editor-bottom-nav glass fixed bottom-[calc(18px+env(safe-area-inset-bottom))] left-1/2 z-20 flex w-[calc(100%-40px)] max-w-[440px] -translate-x-1/2 justify-around gap-1.5 rounded-full border border-hazumu-white/85 p-2"
    aria-label="Editor navigation"
  >
    <NavItem
      icon={<PanelsTopLeft size={20} />}
      label="Site"
      current={!pagesOpen && !designOpen && !mediaOpen && !youOpen && !agentOpen}
      onClick={onOpenSite}
    />
    <NavItem icon={<BookOpen size={20} />} label="Pages" current={!!pagesOpen} onClick={onOpenPages} tour="pages" />
    <NavItem icon={<Palette size={20} />} label="Design" current={!!designOpen} onClick={onOpenDesign} tour="design" />
    <NavItem icon={<Images size={20} />} label="Media" current={!!mediaOpen} onClick={onOpenMedia} />
    {onOpenAgent ? (
      <NavItem
        icon={<MessageSquare size={20} />}
        label="Ducky"
        current={!!agentOpen}
        onClick={onOpenAgent}
      />
    ) : null}
    <NavItem icon={<UserRound size={20} />} label="You" current={!!youOpen} onClick={onOpenYou} />
  </nav>
);
